import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Plus, MoreHorizontal, Pencil, Trash2, Layers } from 'lucide-react'
import { usePricingCategories, usePricingMutations } from '@/hooks/usePricing'
import { CategoryEditDialog } from './CategoryEditDialog'
import type { PricingCategory } from '@/types/pricing'

export function CategoriesTab() {
  const { data: categories, isLoading } = usePricingCategories()
  const mutations = usePricingMutations()
  const [editingCategory, setEditingCategory] = useState<PricingCategory | null>(null)
  const [showCreateDialog, setShowCreateDialog] = useState(false)

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Aircraft Pricing Categories</CardTitle>
              <CardDescription>
                Define aircraft classification tiers for pricing. Aircraft are auto-classified using these rules (first match wins, ordered by sort order).
              </CardDescription>
            </div>
            <Button onClick={() => setShowCreateDialog(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Add Category
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {!categories?.length ? (
            <div className="text-center py-12 text-muted-foreground">
              <Layers className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No categories defined</p>
              <p className="mt-1 text-sm">
                Create categories like "Light Piston", "Light Jet", "Heavy Jet" to tier your pricing.
              </p>
              <Button className="mt-4" onClick={() => setShowCreateDialog(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Category
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[60px]">Order</TableHead>
                  <TableHead>Code</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Classification Rules</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {categories.map((cat) => (
                  <TableRow key={cat.id}>
                    <TableCell className="text-center text-muted-foreground">
                      {cat.sortOrder}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{cat.code}</TableCell>
                    <TableCell className="font-medium">{cat.name}</TableCell>
                    <TableCell>
                      <ClassificationRuleSummary category={cat} />
                    </TableCell>
                    <TableCell>
                      <Badge variant={cat.isActive ? 'default' : 'secondary'}>
                        {cat.isActive ? 'Active' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setEditingCategory(cat)}>
                            <Pencil className="mr-2 h-4 w-4" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive"
                            onClick={() => mutations.removeCategory.mutate(cat.id)}
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CategoryEditDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        category={null}
      />

      <CategoryEditDialog
        open={!!editingCategory}
        onOpenChange={(open) => !open && setEditingCategory(null)}
        category={editingCategory}
      />
    </>
  )
}

function ClassificationRuleSummary({ category }: { category: PricingCategory }) {
  const rules: string[] = []

  if (category.matchBaseCategory) rules.push(`Category: ${category.matchBaseCategory}`)
  if (category.matchEngineCount != null) rules.push(`Engines: ${category.matchEngineCount}`)
  if (category.matchMinWeight != null || category.matchMaxWeight != null) {
    const min = category.matchMinWeight != null ? `${category.matchMinWeight}` : '0'
    const max = category.matchMaxWeight != null ? `${category.matchMaxWeight}` : '+'
    rules.push(`Weight: ${min}-${max} lbs`)
  }
  if (category.matchFuelType) rules.push(`Fuel: ${category.matchFuelType}`)

  if (rules.length === 0) {
    return <span className="text-xs text-muted-foreground italic">Catch-all (no filters)</span>
  }

  return (
    <div className="flex flex-wrap gap-1">
      {rules.map((r) => (
        <Badge key={r} variant="outline" className="text-xs">
          {r}
        </Badge>
      ))}
    </div>
  )
}
